import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { type API } from '@/api/index';
import { ReservationCard } from '@/components/elements/ReservationCard';
import { ReservationDetailsSection } from '@/components/elements/ReservationDetailsSection';
import { ShareDeviceReservationDialog } from '@/components/elements/ShareDeviceDialog';
import { ShareDeviceDialogContentFloorMap } from '@/components/elements/ShareDeviceDialogContentFloorMap';
import { ReservationIntroSection } from '@/components/elements/ShareDeviceReservationIntroSection';
import { ShareDeviceSidebar } from '@/components/elements/ShareDeviceSidebar';
import { SharedDeviceTimeoutNotification } from '@/components/elements/SharedDeviceTimeoutNotification';
import { ShareDeviceLayout } from '@/components/layouts/ShareDeviceLayout';
import { ROUTE_PATH } from '@/constants/routes';
import { SHARED_DEVICE_TIMEOUT_SECONDS } from '@/constants/sharedDevice';
import { useDialog } from '@/hooks/useDialog';
import { useSharedDeviceTimeout } from '@/hooks/useSharedDeviceTimeout';
import { getSharedDeviceFloorMapImagePath } from '@/utils/floorMapPath';

export const ShareDeviceComplete = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const reservation: API['POST']['CreateSharedDeviceReservation']['response']['reservation'] =
    location.state?.reservation;

  // カスタムフックでフロアマップダイアログ管理
  const floorMapDialog = useDialog();

  // フロアマップ画像パスを生成
  const floorMapImagePath = reservation
    ? getSharedDeviceFloorMapImagePath(reservation)
    : null;

  // TOP画面に戻るタイムアウト機能
  const { remainingSeconds, resetTimer } = useSharedDeviceTimeout(
    SHARED_DEVICE_TIMEOUT_SECONDS.COMPLETE,
  );

  useEffect(() => {
    if (!reservation) {
      navigate(ROUTE_PATH.SHARE_DEVICE_TIME_SELECTION);
    }
  }, [reservation, navigate]);

  if (!reservation) return null;

  const handleClose = () => {
    navigate(ROUTE_PATH.SHARE_DEVICE);
  };

  return (
    <ShareDeviceLayout>
      <div className="page page_f6">
        <main className="page__main">
          <div className="page__content">
            <section className="f6">
              <div className="f6__line">
                <div className="f6__inner">
                  <div className="f6__area">
                    <ReservationIntroSection keyType={reservation.key_type} />

                    <div className="f6__content">
                      <ReservationCard
                        reservation={reservation}
                        onOpenFloorMap={floorMapDialog.open}
                        floorMapImagePath={floorMapImagePath}
                      />

                      <ReservationDetailsSection keyType={reservation.key_type} />
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>

        <ShareDeviceSidebar>
          <button
            className="page__side-btn page__side-btn--close js-close-dialog"
            type="button"
            onClick={handleClose}
          >
            {t('shareDevice.complete.closeButton')}
          </button>
        </ShareDeviceSidebar>
      </div>

      {/* マップダイアログ */}
      <ShareDeviceReservationDialog
        className="dialog--map"
        ref={floorMapDialog.dialogRef}
      >
        {floorMapImagePath && (
          <ShareDeviceDialogContentFloorMap
            onClose={floorMapDialog.close}
            floorMapImageUrl={floorMapImagePath}
          />
        )}
      </ShareDeviceReservationDialog>

      {/* タイムアウト警告通知 */}
      <SharedDeviceTimeoutNotification
        remainingSeconds={remainingSeconds}
        onContinue={resetTimer}
      />
    </ShareDeviceLayout>
  );
};
